import {
  InMemoryWebStorage,
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from "oidc-client-ts";
import type { AuthHandle } from "../api/client";
import { appConfig } from "../config";

// OIDC/PKCE against the STAFF realm (ADR 0015 §3.2) — never the customer realm.
//
// Token-storage posture (a deliberate security decision): the access/refresh
// tokens live in an in-memory store (InMemoryWebStorage), NOT localStorage — so
// an XSS foothold can't exfiltrate a persisted token, and a closed tab drops the
// session. Only the transient PKCE/state handshake uses sessionStorage, because
// it must survive the authorization redirect; it carries no long-lived secret.
//
// FLAGGED (realm-dependent): silent renew uses the refresh-token grant
// (automaticSilentRenew). Whether the realm rotates refresh tokens / supports
// iframe silent-renew is a realm-config decision finalized with infra.
function buildSettings(): UserManagerSettings {
  return {
    authority: appConfig.oidc.authority,
    client_id: appConfig.oidc.clientId,
    redirect_uri: appConfig.oidc.redirectUri,
    post_logout_redirect_uri: appConfig.oidc.redirectUri,
    response_type: "code",
    scope: appConfig.oidc.scope,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  };
}

export class OidcAuth implements AuthHandle {
  readonly manager: UserManager;
  private current: User | null = null;

  constructor(manager: UserManager = new UserManager(buildSettings())) {
    this.manager = manager;
    this.manager.events.addUserLoaded((u) => {
      this.current = u;
    });
    this.manager.events.addUserUnloaded(() => {
      this.current = null;
    });
  }

  /** Restore an existing session (if any) on app boot. */
  async init(): Promise<User | null> {
    this.current = await this.manager.getUser();
    return this.current;
  }

  /** Begin the Authorization-Code-+-PKCE login (full redirect to the realm). */
  login(): Promise<void> {
    return this.manager.signinRedirect();
  }

  /** Complete the redirect back from the realm; caches the user in memory. */
  async completeLogin(): Promise<User> {
    this.current = await this.manager.signinRedirectCallback();
    return this.current;
  }

  async logout(): Promise<void> {
    this.current = null;
    await this.manager.signoutRedirect();
  }

  getUser(): User | null {
    return this.current;
  }

  /** Notify on auth-state changes (login callback / silent renew / logout) so the
   *  React tree flips `status` — the in-memory token store means status must follow
   *  the events, not just the boot-time getUser. Returns an unsubscribe fn. */
  onUserChanged(cb: () => void): () => void {
    const handler = (): void => cb();
    this.manager.events.addUserLoaded(handler);
    this.manager.events.addUserUnloaded(handler);
    return () => {
      this.manager.events.removeUserLoaded(handler);
      this.manager.events.removeUserUnloaded(handler);
    };
  }

  getToken(): string | null {
    return this.current?.access_token ?? null;
  }

  async refresh(): Promise<string | null> {
    try {
      const u = await this.manager.signinSilent();
      this.current = u;
      return u?.access_token ?? null;
    } catch {
      return null;
    }
  }

  /** Interactive MFA step-up via a popup (keeps SPA state; returns a strong
   *  token on success). FLAGGED: the acr/amr the realm expects for step-up is
   *  realm-config — `prompt: "login"` forces re-auth; acr_values is wired when
   *  the realm's MFA policy is finalized. */
  async stepUp(): Promise<string | null> {
    try {
      const u = await this.manager.signinPopup({ prompt: "login" });
      this.current = u;
      return u?.access_token ?? null;
    } catch {
      return null;
    }
  }
}
